import { Router, type IRouter, type Request, type Response, type NextFunction } from "express";
import { db, adSettingsTable } from "@workspace/db";
import { eq } from "drizzle-orm";

const router: IRouter = Router();

const ADMIN_PASSWORD = process.env.ADMIN_PASSWORD || "streamhub_admin_2024";
const ADMIN_TOKEN = process.env.ADMIN_TOKEN || "admin_token_streamhub_secret_2024";

function requireAdmin(req: Request, res: Response, next: NextFunction) {
  const auth = req.headers.authorization;
  if (!auth || !auth.startsWith("Bearer ") || auth.slice(7) !== ADMIN_TOKEN) {
    res.status(401).json({ error: "Unauthorized" });
    return;
  }
  next();
}

router.get("/ads/public", async (req: Request, res: Response) => {
  try {
    const rows = await db.select().from(adSettingsTable).limit(1);
    if (rows.length === 0) {
      res.json({ data: { enabled: false, slots: [], globalScript: "", adsensePublisherId: "" } });
    } else {
      const row = rows[0];
      if (!row.enabled) {
        res.json({ data: { enabled: false, slots: [], globalScript: "", adsensePublisherId: "" } });
      } else {
        res.json({ data: row });
      }
    }
  } catch (err) {
    req.log.error({ err }, "Error fetching public ad settings");
    res.status(500).json({ error: "Internal error" });
  }
});

router.post("/login", (req: Request, res: Response) => {
  const { password } = req.body as { password?: string };
  if (password === ADMIN_PASSWORD) {
    res.json({ success: true, token: ADMIN_TOKEN });
  } else {
    res.status(401).json({ error: "Invalid password" });
  }
});

router.get("/ads", requireAdmin, async (req: Request, res: Response) => {
  try {
    const rows = await db.select().from(adSettingsTable).limit(1);
    if (rows.length === 0) {
      const defaultSettings = {
        enabled: true,
        slots: [],
        globalScript: "",
        adsensePublisherId: "",
      };
      const [inserted] = await db.insert(adSettingsTable).values(defaultSettings).returning();
      res.json({ data: inserted });
    } else {
      res.json({ data: rows[0] });
    }
  } catch (err) {
    req.log.error({ err }, "Error fetching ad settings");
    res.status(500).json({ error: "Internal error" });
  }
});

router.put("/ads", requireAdmin, async (req: Request, res: Response) => {
  try {
    const { slots, globalScript, adsensePublisherId, enabled } = req.body as {
      slots?: unknown[];
      globalScript?: string;
      adsensePublisherId?: string;
      enabled?: boolean;
    };

    const rows = await db.select().from(adSettingsTable).limit(1);
    if (rows.length === 0) {
      const [inserted] = await db
        .insert(adSettingsTable)
        .values({
          enabled: enabled ?? true,
          slots: slots ?? [],
          globalScript: globalScript ?? "",
          adsensePublisherId: adsensePublisherId ?? "",
        })
        .returning();
      res.json({ data: inserted });
    } else {
      const [updated] = await db
        .update(adSettingsTable)
        .set({
          enabled: enabled ?? rows[0].enabled,
          slots: slots ?? rows[0].slots,
          globalScript: globalScript ?? rows[0].globalScript,
          adsensePublisherId: adsensePublisherId ?? rows[0].adsensePublisherId,
        })
        .where(eq(adSettingsTable.id, rows[0].id))
        .returning();
      res.json({ data: updated });
    }
  } catch (err) {
    req.log.error({ err }, "Error updating ad settings");
    res.status(500).json({ error: "Internal error" });
  }
});

export default router;
